import { useQueryClient } from "react-query";
import { CircularProgress } from "@mui/material";
import { useFormContext } from "react-hook-form";

import DatePickerField from "../../../Components/Form Fields/date-picker-field";
import SSNField from "../../../Components/Form Fields/ssn-field";
import TextField from "../../../Components/Form Fields/text-field";
import CheckBox from "../../../Components/Form Fields/checkbox";
import PrimaryButton from "../../../Components/Buttons/primary-button";

import renderOptedIn from "./opted-in";

import { useModalContext } from "../../../Contexts/ModalContext";

import { useDeals, useUserAccount } from "../../../CustomHooks/Queries";
import { useOptInCreditReporting } from "../../../CustomHooks/Mutations";

import { validation } from "../../../Utils/validations";
import {
  dispatchAPIErrorToast,
  formatDate,
  getPublicIPAddress,
  replaceNonNumericCharacters,
} from "../../../Utils/helpers";
import { APIQueryKeys, longMonthDateFormat, modalVariants } from "../../../Utils/constants";

export default function renderCreditBuilderOptIn(modalContext, props) {
  const { updateModalComponent, setModalVariant, setModalTitle, setModalClassName } = modalContext;

  updateModalComponent(() => <CreditBuilderOptIn {...props} />);
  setModalVariant(modalVariants.flexDialog);
  setModalTitle("Confirm Your Credit Builder Enrollment");
  setModalClassName("full-modal-width");
}

const CreditBuilderOptIn = ({ onOptIn }) => {
  const modalContext = useModalContext();
  const { popModalStack } = modalContext;

  const { watch, register, handleSubmit, formState, getFieldState } = useFormContext();

  const queryClient = useQueryClient();

  const { data: deals } = useDeals();

  const { data: userAccountData } = useUserAccount();

  const { isLoading: isCreditReportingOptingIn, mutate: OptInCreditReporting } = useOptInCreditReporting({
    onSuccess: () => {
      onOptIn?.();
      queryClient.refetchQueries(APIQueryKeys.creditReporting);
      renderOptedIn(modalContext);
    },
    onError: (err) => dispatchAPIErrorToast({ message: err?.response?.data?.error, key: "creditBuilderOptIn" }),
  });

  const nameOfTheUser = `${userAccountData?.data?.data?.user?.firstName} ${userAccountData?.data?.data?.user?.lastName}`;

  const dob = watch("dob");
  const ssn = watch("ssn");
  const name = watch("name");
  const agreeToTerms = watch("agreeToTerms") ?? false;

  const handleNextButtonClick = async () => {
    OptInCreditReporting({
      ...(!!dob ? { dob } : {}),
      ...(!!ssn ? { ssn: replaceNonNumericCharacters(ssn) } : {}),
      user_ip: await getPublicIPAddress(),
    });
  };

  const handleBackButtonClick = () => popModalStack(1);

  const birthDate = deals?.data?.data?.currentDeal?.birthdate;
  const ssn_token = deals?.data?.data?.currentDeal?.ssn_token__c;

  const nameProps = register("name", {
    required: true,
    validate: (name) =>
      name?.trim() === nameOfTheUser?.trim() || "Please make sure the signature matches the name below.",
  });

  const { error: nameFieldError } = getFieldState(nameProps.name, formState);

  const disableCTAButton =
    isCreditReportingOptingIn || (!dob && !birthDate) || (!ssn && !ssn_token) || !name || !agreeToTerms;

  return (
    <form className="d-flex flex-column flex-grow-1" onSubmit={handleSubmit(handleNextButtonClick)} autoComplete="off">
      <div className="modal-dialog-content flex-grow-1">
        <span className="modal-content-text">Please review and agree to the declaration below.</span>
        <div className="border--rounded-md bg--bg-secondary py-5 px-4 mt-3">
          <div className="modal-content-text mb-4">
            I consent to having Stake Network, Inc., on behalf of my rental community, report my on-time rent payments
            to major credit bureaus.
          </div>
          <div className="modal-content-text mb-4">
            I understand that this program is optional and I am able to opt-out at any time, subject to a 6-month
            waiting period to re-enroll. I also understand that a late or missed rent payment, after enrollment, will
            automatically opt me out of this service, with a 6-month waiting period to re-enroll.
          </div>
          <div className="modal-content-text mb-4">Executed {formatDate(new Date(), longMonthDateFormat)}</div>
          <div className="modal-content-text">By: {nameOfTheUser}</div>
        </div>
        {!birthDate && (
          <div className="mt-4">
            <DatePickerField
              name="dob"
              label="Date of Birth"
              validations={{
                isAValidDate: validation.date.isAValidDate,
                isAbove18YearsOfAge: validation.date.isAbove18YearsOfAge,
              }}
              datePickerProps={{ disableFuture: true }}
              textFieldProps={{ placeholder: "MM/DD/YYYY", fullWidth: true }}
              required={true}
            />
          </div>
        )}
        {!ssn_token && (
          <SSNField
            className="mt-5"
            placeholder="123 45 6789"
            description="Your SSN will be securely handled in compliance with privacy regulations."
          />
        )}
        <span className="modal-content-text mt-5 mb-3">
          Enter your full name to agree to the above declaration and confirm your enrollment.
        </span>
        <TextField
          {...nameProps}
          placeholder="Sign here"
          inputProps={{ style: { fontFamily: "Playball", fontSize: "28px", lineHeight: "28px" } }}
          errorMessage={nameFieldError?.message}
        />
        <span className="modal-content-text mt-2">Sign as "{nameOfTheUser}"</span>
        <div className="d-flex align-items-start gap-2 mt-4">
          <CheckBox {...register("agreeToTerms", { required: true })} checked={agreeToTerms} />
          <p className="text-base-regular color--mist mb-0">
            By checking this box, I attest to have read and agree to the Stake's{" "}
            <a
              className="color--stake-blue"
              href="https://www.stakerent.com/legal/end-user-terms"
              target="_blank"
              rel="noreferrer"
            >
              End User Terms
            </a>{" "}
            and{" "}
            <a
              className="color--stake-blue"
              href="https://www.stakerent.com/legal/privacy-policy"
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
      <div className="action-container split-equally">
        <PrimaryButton disabled={disableCTAButton} type="submit">
          Confirm {isCreditReportingOptingIn && <CircularProgress />}
        </PrimaryButton>
        <PrimaryButton className="secondary-colors" onClick={handleBackButtonClick} type="button">
          Back
        </PrimaryButton>
      </div>
    </form>
  );
};
